/*
 * Personal Digital Spaces LLC ("COMPANY") CONFIDENTIAL
 * Unpublished Copyright (c) 2026 Personal Digital Spaces LLC, All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of COMPANY. The intellectual and technical concepts contained
 * herein are proprietary to COMPANY and may be covered by U.S. and Foreign Patents, patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from COMPANY.  Access to the source code contained herein is hereby forbidden to anyone except current COMPANY employees, managers, contractors or partners who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of  COMPANY.   ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { useState } from 'react'
import RegularTemplate from '@/components/templates/RegularTemplate'
import ItemsSelectionMode from '@/components/batches/ItemsSelectionMode'
import UploadCsvForm from '@/components/batches/UploadCsvForm'
import { samsBuildBatchesUrls } from '@/configs/urls'
import ItemBatchCreator from '@/components/forms/predefined/ItemBatchCreator'
import { getPendingSams } from '@/services/pending-sams'
import { getPendingSamCardProps, getPendingSamTableProps } from '@/pages/PendingSams/handlers'
import PendingCardWithAddRemoveOptions from './components/PendingCardWithAddRemoveOptions'
import {
  createSamBuildBatch,
  createCSVSamBuildBatch
} from '@/services/sams-build-batches'
import {
  BACK_TO_SAMS_BUILD_BATCHES,
  CREATE_BUILD_SAMS_BATCH_TITLE
} from '@/configs/strings/sams-build-batches.strings'
import { tableColumns } from './handlers'
import PendingSamsCustomTable from './components/PendingSamsCustomTable'
import { useNavigate } from 'react-router-dom'
import { useToast } from '@chakra-ui/react'
import { generalBatchStates } from '@/configs/server-enums'
import {
  getFailedDescription,
  getOnErrorTitle,
  getSuccessDescription
} from '@/configs/strings/batches'
import { defaultToastConfig } from '@/configs/toast'

const CreateSamsBuildBatch = () => {
  const toast = useToast()
  const { processing } = generalBatchStates
  const batchType = 'SAMs Build Batch'
  const [itemsSelectionMode, setItemsSelectionMode] = useState('uploadCsv')
  const [isLoading, setIsLoading] = useState(false)
  const navigate = useNavigate()
  const processBatch = async (formData, getAccessToken) => {
    try {
      setIsLoading(true)
      const samBuildBatch = await createSamBuildBatch(formData, getAccessToken)

      if (samBuildBatch.state === processing) {
        toast({
          title: batchType,
          description: getSuccessDescription(
            batchType,
            samBuildBatch.name
          ),
          status: 'success',
          ...defaultToastConfig
        })
      }

      navigate(`/${samsBuildBatchesUrls.base}/${samBuildBatch.id}`, { replace: true })
    } catch (error) {
      toast({
        title: getOnErrorTitle(batchType),
        description: error.response?.data?.message || getFailedDescription(batchType),
        status: 'error',
        ...defaultToastConfig
      })
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }
  const isUploadCsv = itemsSelectionMode === 'uploadCsv'
  const currentCSVCreateSamBuildBatch = async (response) => {
    if (response?.id) {
      navigate(`/${samsBuildBatchesUrls.base}/${response.id}`, { replace: true })
    }
  }
  return (
    <RegularTemplate
      backUrl={`/${samsBuildBatchesUrls.base}`}
      backLabel={BACK_TO_SAMS_BUILD_BATCHES}
      title={CREATE_BUILD_SAMS_BATCH_TITLE}
      loadingContent={isLoading}
    >
      <ItemsSelectionMode
        className="mb-5 inline-block"
        onChange={setItemsSelectionMode}
      />
      { isUploadCsv
        ? <UploadCsvForm
            csvService={createCSVSamBuildBatch}
            getResponse={currentCSVCreateSamBuildBatch}
          />
        : <ItemBatchCreator
            baseUrl={samsBuildBatchesUrls.base}
            customTable={PendingSamsCustomTable}
            getItemsPropsForTable={getPendingSamTableProps}
            getItemsService={getPendingSams}
            hasDataKeyResponse={false}
            itemComponent={PendingCardWithAddRemoveOptions}
            itemIdsKey="licensedAssetIds"
            itemsKey="data"
            redirectToItem={false}
            registerBatchService={processBatch}
            setItemCustomProps={getPendingSamCardProps}
            tableColumns={tableColumns}
          />
      }
    </RegularTemplate>
  )
}

export default CreateSamsBuildBatch
